import {
  deepComputed,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { setError, setLoaded, setLoading, withCallState, withReset } from '@ngrx-toolkit/core';
import { inject } from '@angular/core';
import { LangService } from '@td/i18n';
import type { CourtFeatures, Feature, PleaOptions } from '../../types';
import { ConfigService } from './config.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, type Observable, pipe, skip, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthStore } from '../auth';

type State = {
  courtId: string;
  plea: PleaOptions;
  availableFeatures: Array<Feature>;
  courtFeatures: CourtFeatures | null;
};

const initialState: State = {
  courtId: '',
  plea: [],
  availableFeatures: [],
  courtFeatures: null,
};

export const ConfigStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ availableFeatures }) => ({
    features: deepComputed(() => {
      const features = availableFeatures();
      return {
        payLater: features.includes('PAY_LATER'),
        optionsPage: features.includes('OPTIONS_PAGE'),
      };
    }),
  })),
  withProps(({ courtFeatures }) => ({
    courtFeatures$: toObservable(courtFeatures),
  })),
  withCallState({ collections: ['', 'courtFeatures'] }),
  withReset(),
  withMethods((store, api = inject(ConfigService), lang = inject(LangService)) => ({
    load: rxMethod<void>(
      pipe(
        tap(() => patchState(store, setLoading())),
        switchMap(() =>
          api.load(lang.language()).pipe(
            tapResponse({
              next: (config) =>
                patchState(
                  store,
                  {
                    courtId: config.courtId,
                    plea: config.pleaOptions,
                    availableFeatures: config.features,
                  },
                  setLoaded(),
                ),
              error: (error) => patchState(store, setError(error)),
            }),
          ),
        ),
      ),
    ),
    getCourtFeatures: (): Observable<CourtFeatures> => {
      patchState(store, setLoading('courtFeatures'));
      return api.getCourtFeatures().pipe(
        tap({
          next: (courtFeatures) => patchState(store, { courtFeatures }, setLoaded('courtFeatures')),
          error: (error) => patchState(store, setError(error, 'courtFeatures')),
        }),
      );
    },
  })),
  withHooks({
    onInit(store, lang = inject(LangService), authStore = inject(AuthStore)) {
      lang.lang$
        .pipe(
          skip(1),
          filter(() => store.loaded()),
        )
        .subscribe(() => store.load());
      authStore.logout$.subscribe(() => store.resetState());
    },
  }),
);
